import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from '@material-ui/core';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover }
  },
  drawerBody: {
    width: 560,
    maxWidth: '95vw',
    padding: theme.spacing(3)
  },
  detailLabel: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: 0.5
  },
  attachmentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    marginBottom: theme.spacing(1)
  }
}));

const STATUS_COLORS = {
  New: '#2196f3',
  Received: '#ff9800',
  Approved: '#4caf50'
};

const money = (value) =>
  typeof value === 'number'
    ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const shortDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const StatusChip = ({ status }) => (
  <Chip
    size="small"
    label={status || 'Unknown'}
    style={{ backgroundColor: STATUS_COLORS[status] || '#757575', color: 'white', fontSize: '0.7rem' }}
  />
);

const DetailField = ({ label, value, classes }) => (
  <Grid item xs={6} sm={4}>
    <Typography className={classes.detailLabel}>{label}</Typography>
    <Typography variant="body2">{value ?? '—'}</Typography>
  </Grid>
);

const ReceiptDetailDrawer = ({ receipt, onClose, classes }) => {
  if (!receipt) return null;
  const items = receipt.ReceiptItems || [];
  const extraCosts = (receipt.ReceiptExtraCosts || []).filter((c) => c && (c.ExtraCostType || c.ExtraCost));
  const attachments = receipt.ReceiptAttachments || [];

  return (
    <Drawer anchor="right" open={Boolean(receipt)} onClose={onClose}>
      <div className={classes.drawerBody}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            Receipt {receipt.FormattedReceiptNumber || receipt.ReceiptNumber || receipt.ReceiptID}
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Box display="flex" style={{ gap: 8 }} mb={2}>
          <StatusChip status={receipt.ReceiptStatusName} />
          {receipt.IsPurchaseCredit && <Chip size="small" label="Credit" style={{ backgroundColor: '#9c27b0', color: 'white' }} />}
        </Box>

        <Grid container spacing={2}>
          <DetailField label="Vendor" value={receipt.VendorName} classes={classes} />
          <DetailField label="Total" value={money(receipt.ReceiptTotalCost)} classes={classes} />
          <DetailField label="Branch" value={receipt.BranchName} classes={classes} />
          <DetailField label="Created By" value={receipt.CreatedBy} classes={classes} />
          <DetailField label="Created" value={shortDate(receipt.CreatedDate)} classes={classes} />
          <DetailField label="Received" value={shortDate(receipt.ReceivedDate)} classes={classes} />
          <DetailField label="Approved" value={shortDate(receipt.ApprovedDate)} classes={classes} />
          <DetailField label="Work Ticket" value={receipt.WorkTicketNumber || '—'} classes={classes} />
          <DetailField label="Opportunity" value={receipt.OpportunityNumber || '—'} classes={classes} />
          <DetailField label="Vendor Invoice #" value={receipt.VendorInvoiceNum} classes={classes} />
        </Grid>

        {receipt.PurchaseReceiptNote && (
          <Box mt={2}>
            <Typography className={classes.detailLabel}>Note</Typography>
            <Typography variant="body2">{receipt.PurchaseReceiptNote}</Typography>
          </Box>
        )}

        <Divider style={{ margin: '16px 0' }} />
        <Typography variant="subtitle1" gutterBottom>Line Items ({items.length})</Typography>
        {items.length ? (
          <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit</TableCell>
                  <TableCell align="right">Ext</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, idx) => (
                  <React.Fragment key={item.ReceiptItemID || idx}>
                    <TableRow>
                      <TableCell>{item.ItemName || '—'}{item.ItemType ? ` · ${item.ItemType}` : ''}</TableCell>
                      <TableCell align="right">{item.ItemQuantity ?? '—'}</TableCell>
                      <TableCell align="right">{money(item.ItemUnitCost)}</TableCell>
                      <TableCell align="right">{money(item.ItemExtendedCost)}</TableCell>
                    </TableRow>
                    {(item.ItemAllocations || []).filter((a) => a.WorkTicketNumber || a.InventoryLocationName).map((alloc, ai) => (
                      <TableRow key={ai}>
                        <TableCell colSpan={4} style={{ paddingLeft: 32, fontSize: '0.75rem', color: '#777' }}>
                          ↳ {alloc.WorkTicketNumber ? `WT ${alloc.WorkTicketNumber}` : alloc.InventoryLocationName}
                          {alloc.ItemQuantity != null ? ` · qty ${alloc.ItemQuantity}` : ''}
                          {alloc.ItemTotalCost != null ? ` · ${money(alloc.ItemTotalCost)}` : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">No line items synced.</Typography>
        )}

        {extraCosts.length > 0 && (
          <Box mt={2}>
            <Typography variant="subtitle1" gutterBottom>Extra Costs</Typography>
            {extraCosts.map((cost, idx) => (
              <Typography key={idx} variant="body2">
                {cost.ExtraCostType || 'Extra cost'}: {money(cost.ExtraCost)}
              </Typography>
            ))}
          </Box>
        )}

        <Divider style={{ margin: '16px 0' }} />
        <Typography variant="subtitle1" gutterBottom>
          Attachments ({attachments.length})
        </Typography>
        {attachments.length ? attachments.map((attachment) => (
          <div key={attachment.AttachmentID} className={classes.attachmentRow}>
            <AttachFileIcon fontSize="small" color="action" />
            <Box flexGrow={1} minWidth={0}>
              <Typography variant="body2" noWrap>{attachment.OriginalFileName || attachment.AttachmentName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {attachment.FileExtension || ''} · uploaded {shortDate(attachment.DateUploaded)}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<OpenInNewIcon />}
              onClick={() => window.open(`/api/aspire/receipts/attachments/${attachment.AttachmentID}/download`, '_blank')}
            >
              Open
            </Button>
          </div>
        )) : (
          <Typography variant="body2" color="textSecondary">No attachments on this receipt.</Typography>
        )}
      </div>
    </Drawer>
  );
};

const PurchaseReceiptsPage = () => {
  const classes = useStyles();

  const [docs, setDocs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ statuses: [], branches: [] });
  const [selected, setSelected] = useState(null);

  // Filters
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [branch, setBranch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hasAttachment, setHasAttachment] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    axios.get('/api/aspire/receipts/meta')
      .then(({ data }) => data?.success && setMeta({ statuses: data.statuses, branches: data.branches }))
      .catch(() => {});
  }, []);

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        search,
        status,
        branch,
        dateFrom,
        dateTo,
        hasAttachment,
        page: page + 1,
        limit: rowsPerPage
      };
      const { data } = await axios.get('/api/aspire/receipts', { params });
      if (!data?.success) throw new Error(data?.error || 'Failed to load receipts');
      setDocs(data.docs || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load receipts');
    } finally {
      setLoading(false);
    }
  }, [search, status, branch, dateFrom, dateTo, hasAttachment, page, rowsPerPage]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const resetToFirstPage = (setter) => (value) => {
    setPage(0);
    setter(value);
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatus('');
    setBranch('');
    setDateFrom('');
    setDateTo('');
    setHasAttachment('');
    setPage(0);
  };

  const hasFilters = search || status || branch || dateFrom || dateTo || hasAttachment;

  return (
    <Container maxWidth="xl" className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="h4" style={{ fontWeight: 600 }}>Purchase Receipts</Typography>
        <Typography variant="body2" style={{ opacity: 0.9 }}>
          Aspire purchase receipts synced hourly — click a row for line items and attachments
        </Typography>
      </Box>

      <Paper className={classes.filterBar}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Vendor, receipt#, WT#, creator..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && resetToFirstPage(setSearch)(searchInput)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              }}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              select fullWidth size="small" variant="outlined" label="Status"
              value={status} onChange={(e) => resetToFirstPage(setStatus)(e.target.value)}
            >
              <MenuItem value="">All statuses</MenuItem>
              {meta.statuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2}>
            <TextField
              select fullWidth size="small" variant="outlined" label="Branch"
              value={branch} onChange={(e) => resetToFirstPage(setBranch)(e.target.value)}
            >
              <MenuItem value="">All branches</MenuItem>
              {meta.branches.map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <TextField
              fullWidth size="small" variant="outlined" type="date" label="From"
              InputLabelProps={{ shrink: true }}
              value={dateFrom} onChange={(e) => resetToFirstPage(setDateFrom)(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <TextField
              fullWidth size="small" variant="outlined" type="date" label="To"
              InputLabelProps={{ shrink: true }}
              value={dateTo} onChange={(e) => resetToFirstPage(setDateTo)(e.target.value)}
            />
          </Grid>
          <Grid item xs={6} sm={2} md={1}>
            <TextField
              select fullWidth size="small" variant="outlined" label="Attach"
              value={hasAttachment} onChange={(e) => resetToFirstPage(setHasAttachment)(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">With</MenuItem>
              <MenuItem value="false">Without</MenuItem>
            </TextField>
          </Grid>
          <Grid item>
            <Button variant="contained" color="primary" size="small" onClick={() => resetToFirstPage(setSearch)(searchInput)} disabled={loading}>
              Search
            </Button>
            {hasFilters && (
              <Button size="small" onClick={clearFilters} style={{ marginLeft: 8 }}>
                Clear
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" style={{ marginBottom: 16 }}>{error}</Alert>}

      <Paper>
        {loading && <Box textAlign="center" p={2}><CircularProgress size={28} /></Box>}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 600 }}>Receipt#</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Vendor</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell style={{ fontWeight: 600 }} align="right">Total</TableCell>
                <TableCell style={{ fontWeight: 600 }}>WT#</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Branch</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Created By</TableCell>
                <TableCell style={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell style={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {docs.map((receipt) => (
                <TableRow key={receipt._id} className={classes.tableRow} onClick={() => setSelected(receipt)}>
                  <TableCell>{receipt.FormattedReceiptNumber || receipt.ReceiptNumber || '—'}</TableCell>
                  <TableCell>{receipt.VendorName || '—'}</TableCell>
                  <TableCell><StatusChip status={receipt.ReceiptStatusName} /></TableCell>
                  <TableCell align="right">{money(receipt.ReceiptTotalCost)}</TableCell>
                  <TableCell>{receipt.WorkTicketNumber || '—'}</TableCell>
                  <TableCell>{receipt.BranchName || '—'}</TableCell>
                  <TableCell>{receipt.CreatedBy || '—'}</TableCell>
                  <TableCell>{shortDate(receipt.CreatedDate)}</TableCell>
                  <TableCell padding="none">
                    {receipt.ReceiptHasAttachment && <AttachFileIcon fontSize="small" color="action" />}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ padding: 40 }}>
                    <Typography color="textSecondary">No receipts match these filters</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onChangePage={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      <ReceiptDetailDrawer receipt={selected} onClose={() => setSelected(null)} classes={classes} />
    </Container>
  );
};

export default PurchaseReceiptsPage;
